import { useTranslation } from '@doccareplus/shared/lib';
import { Button, Drawer } from '@doccareplus/ui';
import { PatientRegisterFields } from '../shared/PatientRegisterFields';
import { useRegisterPatientForm } from '../shared/useRegisterPatientForm';

/**
 * RegisterPatientDrawer — desktop register-patient form in a right-side drawer
 * (the design's `sheetIn` sheet), not a centered dialog. Only the name is
 * and phone are required. On success, calls onRegistered with the new id (so the caller can
 * navigate to the chart) and closes.
 */
export function RegisterPatientDrawer({
  onClose,
  onRegistered,
}: {
  onClose: () => void;
  onRegistered?: (patientId: string) => void;
}) {
  const { t } = useTranslation('patients');
  const form = useRegisterPatientForm();

  const handleSubmit = async () => {
    const id = await form.submit();
    if (id) {
      onRegistered?.(id);
      onClose();
    }
  };

  return (
    <Drawer
      title={t('register.title')}
      description={t('register.description')}
      closeLabel={t('common:actions.close')}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" fullWidth onClick={onClose} disabled={form.isSubmitting}>
            {t('register.cancel')}
          </Button>
          <Button type="button" variant="primary" fullWidth onClick={handleSubmit} isLoading={form.isSubmitting}>
            {form.requiresDuplicateReview ? t('register.duplicates.continue') : t('register.submit')}
          </Button>
        </>
      }
    >
      <PatientRegisterFields
        fields={form.fields}
        setField={form.setField}
        error={form.error}
        duplicateMatches={form.duplicateMatches}
        duplicateReason={form.duplicateReason}
        setDuplicateReason={form.setDuplicateReason}
        onSubmit={handleSubmit}
      />
    </Drawer>
  );
}
